import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import "../styles/CreateEventModal.css";
import LocationPicker from "../components/LocationPicker";
import Select from "react-select";
import { useCreateEvent } from "../hooks/useCreateEvent";
import { useUpdateEvent } from "../hooks/useUpdateEvent";
import { useDeleteEvent } from "../hooks/useDeleteEvent";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Domyślny kształt formularza
const DEFAULT_FORM = {
  name: "",
  description: "",
  start_datetime: "",
  end_datetime: "",
  location_type: "online",
  address: "",
  venue_name: "",
  city: "",
  country: "Polska",
  lat: "",
  lng: "",
  link: "",
  price_type: "free", // "free" | "paid"
  price_from: "",
  price_to: "",
  currency: "PLN",
  categories: [],
  type: null,
  image: null,
};

// helper do datetime-local
function toInputDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// mapowanie eventu z API na form
function mapEventToForm(ev) {
  if (!ev) return DEFAULT_FORM;

  let categories = [];
  if (Array.isArray(ev.categories)) {
    categories = ev.categories.map((c) =>
      typeof c === "object" && c !== null ? c.id : c
    );
  }

  return {
    ...DEFAULT_FORM,
    name: ev.name || "",
    description: ev.description || "",
    start_datetime: toInputDate(ev.start_datetime),
    end_datetime: ev.end_datetime ? toInputDate(ev.end_datetime) : "",
    location_type: ev.location_type || "online",
    address: ev.address || "",
    venue_name: ev.venue_name || "",
    city: ev.city || "",
    country: ev.country || "Polska",
    lat: ev.lat ?? "",
    lng: ev.lng ?? "",
    link: ev.link || "",
    price_type: ev.price_type || "free",
    price_from: ev.price_from ?? "",
    price_to: ev.price_to ?? "",
    currency: ev.currency || "PLN",
    categories,
    type: ev.type ?? null,
    image: null,
  };
}

const isBlank = (v) => !v || String(v).trim().length === 0;

const isValidUrl = (value) => {
  if (isBlank(value)) return false;
  try {
    new URL(String(value).trim());
    return true;
  } catch {
    return false;
  }
};

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function getValidationErrors(form, { isEdit }) {
  const errors = {};

  // ALWAYS required
  if (isBlank(form.name)) errors.name = "Podaj nazwę wydarzenia.";
  if (isBlank(form.description)) errors.description = "Podaj opis wydarzenia.";
  if (!form.start_datetime) errors.start_datetime = "Ustaw datę startu.";

  // end (optional) but if provided must be >= start
  if (form.end_datetime && form.start_datetime) {
    const start = new Date(form.start_datetime).getTime();
    const end = new Date(form.end_datetime).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end < start) {
      errors.end_datetime = "Data zakończenia nie może być wcześniejsza niż start.";
    }
  }

  // Categories
  if (!Array.isArray(form.categories) || form.categories.length === 0) {
    errors.categories = "Wybierz co najmniej jedną kategorię.";
  }

  // Location-dependent
  if (form.location_type === "online") {
    if (!isValidUrl(form.link)) errors.link = "Podaj poprawny link (np. https://...).";
  }
  if (form.location_type === "live") {
    if (isBlank(form.address)) errors.address = "Podaj adres dla wydarzenia na żywo.";
  }

  // Price-dependent
  if (form.price_type === "paid") {
    const pf = parseNumber(form.price_from);
    if (pf === null || pf <= 0) {
      errors.price_from = "Dla płatnego wydarzenia podaj „Cena od” (> 0).";
    }
  }

  // Image required only on create (per “wypełnić wszystkie pola”)
  if (!isEdit && !form.image) {
    errors.image = "Dodaj baner wydarzenia.";
  }

  return errors;
}

export default function CreateEventModal({
  mode = "create",
  eventId = null,
  initialData = null,
  modalId = "createEventModal",
  onSuccess,
}) {
  const isEdit = mode === "edit";

  const { createEvent } = useCreateEvent();
  const { updateEvent } = useUpdateEvent();
  const { deleteEvent } = useDeleteEvent();

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initialData ? mapEventToForm(initialData) : DEFAULT_FORM);

  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [organizerConfirmed, setOrganizerConfirmed] = useState(false);

  const title = isEdit ? "Edytuj wydarzenie" : "Dodaj wydarzenie";
  const subtitle = isEdit ? "Zaktualizuj szczegóły wydarzenia" : "Wypełnij szczegóły wydarzenia";
  const submitLabel = isEdit ? "Zapisz zmiany" : "Dodaj wydarzenie";

  // Validation (live)
  const errors = useMemo(() => getValidationErrors(form, { isEdit }), [form, isEdit]);
  const isFormValid = Object.keys(errors).length === 0;
  const canSubmit = !loading && isFormValid && (isEdit || organizerConfirmed);

  const markTouched = (field) =>
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

  const markAllTouched = () => {
    const base = {
      name: true,
      description: true,
      start_datetime: true,
      end_datetime: true,
      categories: true,
      location_type: true,
      link: true,
      address: true,
      price_type: true,
      price_from: true,
      image: true,
    };

    // Only mark relevant fields as touched (prevents showing irrelevant errors)
    if (form.location_type === "online") base.address = false;
    if (form.location_type === "live") base.link = false;
    if (form.price_type === "free") base.price_from = false;

    // In edit, image is optional
    if (isEdit) base.image = false;

    setTouched((prev) => ({ ...prev, ...base }));
  };

  const showFieldError = (field) => (touched[field] || submitAttempted) && errors[field];
  const inputClass = (field) => (showFieldError(field) ? "is-invalid" : "");

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await fetch(`${API_BASE}/api/events/categories/`);
        if (!res.ok) throw new Error("Błąd pobierania kategorii");
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        console.error(err);
        setError("Nie udało się pobrać kategorii.");
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Load event on edit (if no initialData)
  useEffect(() => {
    const loadEvent = async () => {
      if (!isEdit) return;
      if (initialData) return;
      if (!eventId) return;

      try {
        setLoadingEvent(true);
        const res = await fetch(`${API_BASE}/api/events/${eventId}/`);
        if (!res.ok) throw new Error("Błąd pobierania wydarzenia");
        const data = await res.json();
        setForm(mapEventToForm(data));
        setTouched({});
        setSubmitAttempted(false);
      } catch (err) {
        console.error(err);
        setError("Nie udało się wczytać danych wydarzenia.");
      } finally {
        setLoadingEvent(false);
      }
    };

    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, eventId]);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setForm(mapEventToForm(initialData));
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [initialData]);

  // Reset checkbox on modal open (create)
  useEffect(() => {
    const el = document.getElementById(modalId);
    if (!el) return;

    const onShow = () => {
      setError("");
      setSuccess(false);
      setSubmitAttempted(false);
      setTouched({});
      if (!isEdit) setOrganizerConfirmed(false);
    };

    el.addEventListener("show.bs.modal", onShow);
    return () => el.removeEventListener("show.bs.modal", onShow);
  }, [modalId, isEdit]);

  const handleChange = (field, value) => {
    setError("");
    setSuccess(false);

    // UX: clear dependent fields on switches
    if (field === "location_type") {
      if (value === "online") {
        setForm((prev) => ({
          ...prev,
          location_type: "online",
          address: "",
          venue_name: "",
          city: "",
          country: "Polska",
          lat: "",
          lng: "",
        }));
        return;
      }
      if (value === "live") {
        setForm((prev) => ({
          ...prev,
          location_type: "live",
          link: "",
        }));
        return;
      }
    }

    if (field === "price_type") {
      if (value === "free") {
        setForm((prev) => ({
          ...prev,
          price_type: "free",
          price_from: "",
          price_to: "",
        }));
        return;
      }
      if (value === "paid") {
        setForm((prev) => ({
          ...prev,
          price_type: "paid",
        }));
        return;
      }
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    setError("");
    setSuccess(false);

    // Enforce checkbox on create
    if (!isEdit && !organizerConfirmed) {
      setError("Aby dodać wydarzenie, potwierdź że jesteś organizatorem tego wydarzenia.");
      return;
    }

    // Enforce validation
    const currentErrors = getValidationErrors(form, { isEdit });
    if (Object.keys(currentErrors).length > 0) {
      markAllTouched();
      // pokazujemy “pierwszy” błąd jako alert + reszta inline
      const firstMsg = currentErrors[Object.keys(currentErrors)[0]];
      setError(firstMsg || "Uzupełnij wymagane pola.");
      return;
    }

    setLoading(true);

    // payload → kopia form
    const payload = { ...form };

    // cleaning for backend consistency
    if (payload.location_type === "online") {
      payload.address = "";
      payload.venue_name = "";
      payload.city = "";
      payload.country = "Polska";
      payload.lat = null;
      payload.lng = null;
    }

    if (payload.location_type === "live") {
      payload.link = "";
    }

    if (payload.price_type === "free") {
      payload.price_from = "";
      payload.price_to = "";
    }

    try {
      let result;
      if (isEdit && eventId) {
        result = await updateEvent(eventId, payload);
      } else {
        result = await createEvent(payload);
      }

      if (result?.success) {
        setSuccess(true);
        if (typeof onSuccess === "function") {
          onSuccess(result.data || null);
        }
      } else {
        setError(result?.message || "Nie udało się zapisać wydarzenia.");
      }
    } catch (err) {
      console.error(err);
      setError("Wystąpił nieoczekiwany błąd podczas zapisu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !eventId) return;

    const sure = window.confirm(
      "Na pewno chcesz usunąć to wydarzenie? Tej operacji nie można cofnąć."
    );
    if (!sure) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const result = await deleteEvent(eventId);

      if (result?.success) {
        if (typeof onSuccess === "function") {
          onSuccess(null);
        }
      } else {
        setError(result?.message || "Nie udało się usunąć wydarzenia.");
      }
    } catch (err) {
      console.error(err);
      setError("Wystąpił błąd podczas usuwania wydarzenia.");
    } finally {
      setLoading(false);
    }
  };

  // react-select value
  const selectedCategories = categories
    .filter((cat) => form.categories.includes(cat.id))
    .map((cat) => ({ value: cat.id, label: cat.name }));

  const showSummaryErrors =
    (submitAttempted || Object.values(touched).some(Boolean)) && !isFormValid;

  const summaryList = Object.entries(errors)
    .filter(([field]) => {
      if (field === "link" && form.location_type !== "online") return false;
      if (field === "address" && form.location_type !== "live") return false;
      if (field === "price_from" && form.price_type !== "paid") return false;
      if (field === "image" && isEdit) return false;
      return true;
    })
    .map(([, msg]) => msg);

  return createPortal(
    <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content cem-shell border-0 rounded-4 shadow-lg">
          <button
            type="button"
            className="btn-close cem-close position-absolute top-0 end-0 m-3"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>

          <div className="modal-body cem-body">
            <h3 className="cem-title fw-bold mb-1 text-center">{title}</h3>
            <p className="cem-subtitle text-center small mb-4">{subtitle}</p>

            {/* {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}
            {success && (
              <div className="alert alert-success py-2 small mb-3">
                Wydarzenie zapisane pomyślnie.
              </div>
            )} */}

            {/* {showSummaryErrors && summaryList.length > 1 && (
              <div className="alert alert-warning py-2 small mb-3">
                <div className="fw-semibold mb-1">Uzupełnij wymagane pola:</div>
                <ul className="mb-0 ps-3">
                  {summaryList.slice(0, 6).map((m, idx) => (
                    <li key={idx}>{m}</li>
                  ))}
                  {summaryList.length > 6 && <li>…i więcej</li>}
                </ul>
              </div>
            )} */}

            {loadingEvent ? (
              <div className="text-center cem-muted py-4">Ładowanie danych wydarzenia...</div>
            ) : (
              <>
                {/* Nazwa */}
                <div className="mb-3">
                  <label className="form-label cem-label">
                    Nazwa wydarzenia <span className="text-danger">*</span>
                  </label>
                  <input
                    className={`form-control cem-input ${inputClass("name")}`}
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    onBlur={() => markTouched("name")}
                  />
                  {showFieldError("name") && (
                    <div className="invalid-feedback d-block">{errors.name}</div>
                  )}
                </div>

                {/* Opis */}
                <div className="mb-3">
                  <label className="form-label cem-label">
                    Opis <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control cem-input ${inputClass("description")}`}
                    rows="3"
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    onBlur={() => markTouched("description")}
                  />
                  {showFieldError("description") && (
                    <div className="invalid-feedback d-block">{errors.description}</div>
                  )}
                </div>

                {/* Kategorie */}
                <div className="mb-3">
                  <label className="form-label cem-label fw-semibold">
                    Kategorie (max 3) <span className="text-danger">*</span>
                  </label>

                  {loadingCategories ? (
                    <div className="cem-muted small">Ładowanie kategorii...</div>
                  ) : (
                    <>
                      <div
                        className={`cem-selectWrap ${
                          showFieldError("categories") ? "cem-selectWrap--invalid" : ""
                        }`}
                        onBlur={() => markTouched("categories")}
                      >
                        <Select
                          isMulti
                          classNamePrefix="rs"
                          placeholder="Wybierz kategorie..."
                          options={categories.map((cat) => ({
                            value: cat.id,
                            label: cat.name,
                          }))}
                          onChange={(selected) => {
                            const limited = selected?.slice(0, 3) || [];
                            handleChange(
                              "categories",
                              limited.map((opt) => opt.value)
                            );
                            markTouched("categories");
                          }}
                          value={selectedCategories}
                        />
                      </div>

                      {showFieldError("categories") && (
                        <div className="invalid-feedback d-block">{errors.categories}</div>
                      )}
                    </>
                  )}
                </div>

                {/* Daty */}
                <div className="row mb-3 g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label cem-label">
                      Start <span className="text-danger">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control cem-input ${inputClass("start_datetime")}`}
                      value={form.start_datetime}
                      onChange={(e) => handleChange("start_datetime", e.target.value)}
                      onBlur={() => markTouched("start_datetime")}
                    />
                    {showFieldError("start_datetime") && (
                      <div className="invalid-feedback d-block">{errors.start_datetime}</div>
                    )}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label cem-label">Koniec (opcjonalnie)</label>
                    <input
                      type="datetime-local"
                      className={`form-control cem-input ${inputClass("end_datetime")}`}
                      value={form.end_datetime}
                      onChange={(e) => handleChange("end_datetime", e.target.value)}
                      onBlur={() => markTouched("end_datetime")}
                    />
                    {showFieldError("end_datetime") && (
                      <div className="invalid-feedback d-block">{errors.end_datetime}</div>
                    )}
                  </div>
                </div>

                {/* Lokalizacja */}
                <div className="mb-3">
                  <label className="form-label cem-label">
                    Miejsce <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select cem-input ${inputClass("location_type")}`}
                    value={form.location_type}
                    onChange={(e) => handleChange("location_type", e.target.value)}
                    onBlur={() => markTouched("location_type")}
                  >
                    <option value="online">Online</option>
                    <option value="live">Na żywo</option>
                  </select>
                </div>

                {form.location_type === "online" ? (
                  <div className="mb-3">
                    <label className="form-label cem-label">
                      Link <span className="text-danger">*</span>
                    </label>
                    <input
                      className={`form-control cem-input ${inputClass("link")}`}
                      value={form.link}
                      onChange={(e) => handleChange("link", e.target.value)}
                      onBlur={() => markTouched("link")}
                      placeholder="https://twoja-strona.pl"
                    />
                    {showFieldError("link") && (
                      <div className="invalid-feedback d-block">{errors.link}</div>
                    )}
                  </div>
                ) : (
                  <div className="cem-locationPicker">
                    <div className="mb-2">
                      <div className="cem-muted small">
                        Wybierz lokalizację na mapie lub wyszukaj adres.
                      </div>
                    </div>

                    <div
                      className={showFieldError("address") ? "cem-locationPicker--invalid" : ""}
                      onBlur={() => markTouched("address")}
                    >
                      <LocationPicker
                        initialLocation={{
                          address: form.address,
                          venue_name: form.venue_name,
                          city: form.city,
                          country: form.country,
                          lat: form.lat,
                          lng: form.lng,
                        }}
                        onSelect={(loc) => {
                          setError("");
                          setSuccess(false);
                          setForm((prev) => ({
                            ...prev,
                            address: loc.address,
                            venue_name: loc.venue_name,
                            city: loc.city,
                            country: loc.country || "Polska",
                            lat: loc.lat,
                            lng: loc.lng,
                          }));
                          markTouched("address");
                        }}
                      />
                    </div>

                    {showFieldError("address") && (
                      <div className="invalid-feedback d-block">{errors.address}</div>
                    )}
                  </div>
                )}

                {/* Cena */}
                <div className="mb-3">
                  <label className="form-label cem-label">
                    Cena <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select cem-input ${inputClass("price_type")}`}
                    value={form.price_type}
                    onChange={(e) => handleChange("price_type", e.target.value)}
                    onBlur={() => markTouched("price_type")}
                  >
                    <option value="free">Za darmo</option>
                    <option value="paid">Płatne</option>
                  </select>
                </div>

                {form.price_type === "paid" && (
                  <div className="row mb-3 g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label cem-label">
                        Cena od <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control cem-input ${inputClass("price_from")}`}
                        placeholder="Cena od"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.price_from}
                        onChange={(e) => handleChange("price_from", e.target.value)}
                        onBlur={() => markTouched("price_from")}
                      />
                      {showFieldError("price_from") && (
                        <div className="invalid-feedback d-block">{errors.price_from}</div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label cem-label">Cena do (opcjonalnie)</label>
                      <input
                        className="form-control cem-input"
                        placeholder="Cena do"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.price_to}
                        onChange={(e) => handleChange("price_to", e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Baner */}
                <div className="mb-4">
                  <label className="form-label cem-label">
                    Baner {!isEdit && <span className="text-danger">*</span>}{" "}
                    {isEdit && <span className="cem-muted small">(wybierz plik, jeśli zmieniasz)</span>}
                  </label>

                  <input
                    className={`form-control cem-input ${inputClass("image")}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleChange("image", e.target.files?.[0] || null)}
                    onBlur={() => markTouched("image")}
                  />
                  {showFieldError("image") && (
                    <div className="invalid-feedback d-block">{errors.image}</div>
                  )}
                </div>

                {/* Potwierdzenie organizatora (tylko create) */}
                {!isEdit && (
                  <div className="cem-confirm mb-3">
                    <label className="cem-confirmLabel">
                      <input
                        type="checkbox"
                        className="cem-confirmInput"
                        checked={organizerConfirmed}
                        onChange={(e) => setOrganizerConfirmed(e.target.checked)}
                      />
                      <span>Potwierdzam, że jestem organizatorem tego wydarzenia</span>
                    </label>

                    {submitAttempted && !organizerConfirmed && (
                      <div className="invalid-feedback d-block mt-2">
                        To potwierdzenie jest wymagane, aby dodać wydarzenie.
                      </div>
                    )}
                  </div>
                )}

                {/* Przyciski na dole */}
                <div className="cem-actions d-flex gap-3 flex-wrap align-items-center">
                  {isEdit && eventId && (
                    <button
                      type="button"
                      className="btn btn-outline-danger cem-btnDanger"
                      onClick={handleDelete}
                      disabled={loading}
                    >
                      Usuń wydarzenie
                    </button>
                  )}

                  <button
                    className="btn cem-btnPrimary py-2 fw-bold rounded-pill"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    title={
                      canSubmit
                        ? ""
                        : !isEdit && !organizerConfirmed
                        ? "Zaznacz potwierdzenie organizatora."
                        : "Uzupełnij wymagane pola."
                    }
                  >
                    {loading ? "Zapisywanie..." : submitLabel}
                  </button>

                  {/* {!canSubmit && (
                    <div className="cem-muted small">
                      {!isEdit && !organizerConfirmed
                        ? "Zaznacz potwierdzenie organizatora, aby dodać wydarzenie."
                        : "Uzupełnij wymagane pola, aby kontynuować."}
                    </div>
                  )} */}
                </div>
                {/* Globalne komunikaty na dole (pod akcjami) */}
                  <div className="mt-3">
                    {success && (
                      <div className="alert alert-success py-2 small mb-2">
                        Wydarzenie zapisane pomyślnie.
                      </div>
                    )}

                    {error && (
                      <div className="alert alert-danger py-2 small mb-2">
                        {error}
                      </div>
                    )}

                    {showSummaryErrors && summaryList.length > 1 && (
                      <div className="alert alert-warning py-2 small mb-0">
                        <div className="fw-semibold mb-1">Uzupełnij wymagane pola:</div>
                        <ul className="mb-0 ps-3">
                          {summaryList.slice(0, 6).map((m, idx) => (
                            <li key={idx}>{m}</li>
                          ))}
                          {summaryList.length > 6 && <li>…i więcej</li>}
                        </ul>
                      </div>
                    )}
                  </div>

              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
